import { useMemo, useState } from "react";
import StatusItem from "../../status-card/status-item/status-item";
import {
  P2Button,
  P2ButtonGroup,
  P2TextLabel,
  P2TextDescription,
} from "react-bootstrap-common-script";
import StatusTag from "../../status-tag/status-tag";
import { TextIcon } from "../../text/text-icon";
import PendingTaskCard from "../../pending-task-card/pending-task-card";
import ClockPendingTaskSvg from "../../../assets/svg/clock-pending-task";
import { StagesFlowPendingTask } from "../stages-flow";
import { Helpers } from "../../../utils/helper";
import useIsMobile from "../../../hooks/use-is-mobile";
import expiryDuration from "../../../config/expiry_duration";
import { useAppSelector } from "../../../redux/hook";
import { useGetStageD1ObjQuery } from "../../../redux/services/getStageD";
import moment from "moment";

export enum StagesEnum {
  A = "A",
  B = "B",
  C = "C",
  D = "D",
  E = "E",
}
interface StagesProps {
  applicationId?: string;
  stage: StagesEnum;
  activeStage?: string;
  pendingTasks: StagesFlowPendingTask[];
  isStarted: boolean;
  completedAt?: string;
  paymentCompletedAt?: string;
  applicationSubmittedAt?: string;
  canCancelApplication: boolean;
  stageCTA: (value: string) => void;
  cancelCTA: () => void;
}

const getCompletedButtonText = (stage: StagesEnum) => {
  switch (stage) {
    case "A":
      return "View Application";
    case "B":
      return "View Consent";
    case "C":
      return "View Acknowledgement";
    case "D":
      return "View IPA Letter";
    case "E":
      return "View Completion Letter";
    default:
      return "View Application";
  }
};
const getButtonText = (status: string, stage: StagesEnum) => {
  if (status.toLowerCase() === "pending") {
    return "Continue";
  } else if (status.toLowerCase() === "completed") {
    return getCompletedButtonText(stage);
  } else {
    return "Start";
  }
};

const handleStageECompletedStatus = (
  completedAt: string | undefined,
  isStarted: boolean
) => {
  if (completedAt) {
    return "completed";
  } else if (isStarted) {
    return "pending";
  } else {
    return "";
  }
};

const getStatus = (
  stage: StagesEnum,
  activeStage: string | undefined,
  completedAt: string | undefined,
  isStarted: boolean,
  paymentCompletedAt: string | undefined,
  applicationSubmitedAt: string | undefined,
  isOverdue?: boolean
) => {
  
  if (!activeStage) {
    return "";
  } else if (activeStage === stage) {
    if(stage === "B"){
      const softOverDueDay = expiryDuration.trackerStageB;
      const daysAgo = applicationSubmitedAt ? Helpers.formatGetDayAgo(applicationSubmitedAt) : 0;

      if(daysAgo > softOverDueDay){
        return "overdue";
      }
    }
    
    if(stage === "D"){
      // const softOverDueDay = expiryDuration.trackerStageD;
      // const daysAgo = paymentCompletedAt ? Helpers.formatGetDayAgo(paymentCompletedAt) : 0;

      if(isOverdue){
        return "overdue";
      }
    }

    if (stage !== "E") {
      return isStarted ? "pending" : "";
    } else {
      return handleStageECompletedStatus(completedAt, isStarted);
    }

    
  } else if (activeStage > stage) {
    return "completed";
  } else {
    return "";
  }
};

const getTitle = (stage: StagesEnum) => {
  switch (stage) {
    case "A":
      return "Start Application";
    case "B":
      return "Gather Consent";
    case "C":
      return "Make Payment";
    case "D":
      return "Process Application";
    case "E":
      return "Prepare for Completion";
    default:
      return "";
  }
};

const getCompleteDurationText = (stage: StagesEnum, completedAt: string) => {
  switch (stage) {
    case "A":
      return `Application created on ${Helpers.formatDate(
        completedAt ?? new Date().toISOString().split("T")[0]
      )}`;
    case "B":
      return `All consents gathered on ${Helpers.formatDate(
        completedAt ?? new Date().toISOString().split("T")[0]
      )}`;
    case "C":
      return `Payment made on ${Helpers.formatDate(
        completedAt ?? new Date().toISOString().split("T")[0]
      )}`;
    case "D":
      return `In-Principle Approval is obtained on ${Helpers.formatDate(
        completedAt ?? new Date().toISOString().split("T")[0]
      )}`;
    case "E":
      return `Completion appointment is attended on ${Helpers.formatDate(
        completedAt ?? new Date().toISOString().split("T")[0]
      )}`;
    default:
      return "";
  }
};

const getIncompleteDurationText = (stage: StagesEnum) => {
  switch (stage) {
    case "A":
      return "15 minutes to complete";
    case "B":
      return "Up to 1 week";
    case "C":
      return "5 mins to complete";
    case "D":
      return "Up to 2 months";
    case "E":
      return "Up to 2 weeks";
    default:
      return "";
  }
};

const getDurationText = (
  stage: StagesEnum,
  status: string,
  completedAt?: string
) => {
  if (status.toLowerCase() === "completed" && completedAt) {
    return getCompleteDurationText(stage, completedAt);
  } else {
    return getIncompleteDurationText(stage);
  }
};

const getDescription = (
  stage: StagesEnum,
  isContentExpanded: boolean,
  setIsContentExpanded: (value: boolean) => void
) => {
  switch (stage) {
    case "A":
      if (isContentExpanded === false) {
        return (
          <div>
           All outstanding payments to the flat, such as to HDB, Town Councils and IRAS, should be fully paid before submitting the application. Applicants will not be able to complete the change in flat ownership if there are outstanding payments to the flat....
            <span style={{ color: "#0C8188", cursor: "pointer" }}
              onClick={() => {
                setIsContentExpanded(!isContentExpanded);
              }}
            >
              see more
            </span>
          </div>
        );
      } else {
        return (
          <div>
            <p style={{ marginBottom: 32 }}>
              All outstanding payments to the flat, such as to HDB, Town Councils and IRAS, should be fully paid before submitting the application. Applicants will not be able to complete the change in flat ownership if there are outstanding payments to the flat.
            </p>
            <p style={{ marginBottom: 32 }}>
              If there is an existing mortgage loan and/or refund to the withdrawing owner's CPF account for the amount used for the flat, applicants must ensure full payment before the completion of the ownership change. If the proposed owner(s) are agreeable, they may decide to use their CPF savings (subject to CPF limits), or obtain a fresh mortgage loan to pay the existing mortgage loan and/ or CPF refund (except where the Court specify that the CPF refund is to be borne by the withdrawing owner).
            </p>
          </div>
        );
      }
    case "B":
      return "All current and proposed owners must be aware of and give consent to the change in flat ownership. Once the application is created, all applicants will be given 7 days to submit their consent. Otherwise, your application will be cancelled.";
    case "C":
      return "After payment is made, the application will be submitted to HDB for review.";
    case "D":
      return "Upon reviewing your application, you will be provided with subsequent updates on the application status. Also, applicants will be able to retrieve the financial plan on the payments required for the change in flat ownership at this stage.";
    case "E":
      return "HDB will arrange for completion appointment and applicants may review the documents that they would be required to sign at completion prior to attending the appointment. Please also bring along all original documents for the completion.";
    default:
      return "";
  }
};

function Stages({
  stage,
  applicationId,
  activeStage,
  pendingTasks,
  isStarted,
  completedAt,
  canCancelApplication,
  paymentCompletedAt,
  applicationSubmittedAt,
  stageCTA,
  cancelCTA,
}: StagesProps) {
  const { isMobile } = useIsMobile();
  const [isContentExpanded, setIsContentExpanded] = useState<boolean>(false);
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);
  const { data: StageDData } = useGetStageD1ObjQuery({ application_uuid: applicationId ?? "", uinfin: loginUser?.uinfin ?? "", stage: "D1", singpass_token: singpassToken ?? "" },{ skip: (!applicationId || stage !== "D" || activeStage !== "D") });

  const cancellableStages = useMemo(() => {
      return ["A", "B", "C"];
  }, [])
  const isOverdue = useMemo(() => {
    let isStageDOverdue: boolean = false;
    if(StageDData){
      const currentDate = moment();
      let requestsKeyDates: any = [];
      if(StageDData?.message_info){
        requestsKeyDates = requestsKeyDates.concat(StageDData?.message_info
          .map((item) => {
            return {
              received_date: item.received_date,
              due_date: item.due_date,
              type: "message"
            };
          }))
      }
  
      if(StageDData?.solicitor_message_info){
        requestsKeyDates = requestsKeyDates.concat(StageDData?.solicitor_message_info.map((item) => {
          return {
            received_date: item.received_date,
            due_date: item.due_date,
            type: "solicitor_message"
          };
        }))
      }
  
      if(StageDData?.folders){
        requestsKeyDates = requestsKeyDates.concat(StageDData?.folders.map((item) => {
          return {
            received_date: item.received_date,
            due_date: item.due_date,
            type: "document"
          };
        }))
      }
      let daysDifference: number = -1;
      for(const keyDate of requestsKeyDates){
        const lapsedDuration = moment(currentDate).diff(moment(keyDate.received_date), "days");
        if(lapsedDuration > daysDifference){
            daysDifference = lapsedDuration;
        }
      }
      if(daysDifference > expiryDuration.hdb_review){
         isStageDOverdue = true;
      }
    }

    return isStageDOverdue;
  }, [StageDData])

  const status = useMemo(() => {
    return getStatus(stage, activeStage, completedAt, isStarted, paymentCompletedAt, applicationSubmittedAt, isOverdue);
  }, [stage, activeStage, completedAt, isStarted, paymentCompletedAt, isOverdue]);

  const isLineActive = activeStage ? activeStage >= stage : false;
  const isActive = activeStage ? activeStage === stage : false;

  const showPendingTask =
    status.toLowerCase() === "pending" && pendingTasks.length > 0 && stage !== "D" && stage !== "E";

  const getFlowItemUpperPartClass = useMemo(() => {
    const alphabetArray = ["A", "B", "C", "D", "E"];
    const activeStageIndex = alphabetArray.findIndex(
      (item) => item === activeStage
    );
    const currentStageIndex = alphabetArray.findIndex((item) => item === stage);
    if (currentStageIndex <= activeStageIndex + 1) {
      return "polymer-stages-flow-line__upper polymer-stages-flow-line__upper__active";
    } else {
      return "polymer-stages-flow-line__upper polymer-stages-flow-line__upper__inactive";
    }
  }, [activeStage, stage]);

  const getFlowLineAdditionalClass = useMemo(() => {
    switch (stage) {
      case "A":
        return "polymer-stages-flow-line polymer-stages-flow-line__first";
      case "E":
        return "polymer-stages-flow-line__last";
      default:
        return "";
    }
  }, [stage]);

  return (
    <div className="polymer-stages-flow-item">
      <div className={`polymer-stages-flow-item__dot-wrapper`}>
        {stage !== "A" && <div className={getFlowItemUpperPartClass}></div>}
        <div
          className={`polymer-stages-flow-line ${
            isLineActive ? "polymer-stages-flow-line__active" : ""
          } ${getFlowLineAdditionalClass}`}
        />
        <StatusItem
          variant={stage === "A" ? "default" : "dot"}
          isActive={isLineActive}
        />
      </div>
      <div
        className={`polymer-stages-flow-item__content-wrapper ${
          showPendingTask &&
          "polymer-stages-flow-item__content-wrapper-no-max-width"
        }`}
        style={{ width: showPendingTask ? "fit-content" : "100%" }}
      >
        <div>
          {isActive && <P2TextLabel bold="700" label={"You are here!"} />}
          <div
            aria-disabled={!isLineActive}
            className={`polymer-stages-flow-item__content-container ${
              !isLineActive
                ? "polymer-stages-flow-item__content-container__disabled"
                : ""
            }`}
          >
            <div
              className={`polymer-stages-flow-item__content-container-full-width ${
                showPendingTask &&
                "polymer-stages-flow-item__content-container-no-max-width"
              }`}
            >
              <div className="polymer-stages-flow-item__stage-row">
                <div className="polymer-stages-flow-item__stage-row-left">
                  <P2TextLabel bold="700" label={`Stage ${stage}`} />
                  <StatusTag status={status}/>
                </div>
                <TextIcon
                  gap={8}
                  leading={22}
                  weight={500}
                  size={16}
                  icon={<ClockPendingTaskSvg />}
                  title={getDurationText(stage, status, completedAt)}
                />
              </div>
              {status.toLowerCase() !== "completed" && (
                <div className="polymer-stages-flow-item__content-container__title-description">
                  <P2TextDescription
                    color={"#333333"}
                    title={getTitle(stage)}
                    description={getDescription(
                      stage,
                      isContentExpanded,
                      setIsContentExpanded
                    )}
                  />
                </div>
              )}
              {isLineActive && !isMobile && (
                <div className="polymer-stages-flow-item__content-container__button">
                  <P2ButtonGroup>
                    {status.toLowerCase() === "pending" &&
                      canCancelApplication && cancellableStages.includes(stage) && (
                        <P2Button
                          onClick={cancelCTA}
                          variant={"outline-primary"}
                        >
                          Cancel application
                        </P2Button>
                      )}
                    <P2Button
                      onClick={stageCTA(stage)}
                      variant={
                        status.toLowerCase() === "completed"
                          ? "outline-primary"
                          : "primary-1"
                      }
                    >
                      {getButtonText(status, stage)}
                    </P2Button>
                  </P2ButtonGroup>
                </div>
              )}
            </div>
            {showPendingTask && (
              <PendingTaskCard
                cardTitle="Actions required"
                pendingTask={pendingTasks}
              />
            )}
            {isLineActive && isMobile && (
              <div className="polymer-stages-flow-item__content-container__button">
                <P2ButtonGroup>
                  {status.toLowerCase() === "pending" &&
                    canCancelApplication && cancellableStages.includes(stage) && (
                      <P2Button onClick={cancelCTA} variant={"outline-primary"}>
                        Cancel application
                      </P2Button>
                    )}
                  <P2Button
                    onClick={stageCTA(stage)}
                    variant={
                      status.toLowerCase() === "completed"
                        ? "outline-primary"
                        : "primary-1"
                    }
                  >
                    {getButtonText(status, stage)}
                  </P2Button>
                </P2ButtonGroup>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Stages;
